import { useMutation, useQuery } from '@apollo/client';
import { ArrowLeftStartOnRectangleIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import AuthLayout from 'layouts/AuthLayout';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';

import alIndiaLogo from 'media/alIndiaLogo.png';

const VaultDocument = () => {
  const { documentId } = useParams<{ documentId: string }>();

  const LOGIN_VAULT_ACCOUNT_MUTATION = gql(`
        mutation LoginDocumentVaultAccount($email: String!, $password: String!) {
            loginDocumentVaultAccount(email: $email, password: $password) {
                id
                email
                expiresAt
                sessionToken
            }
        }
    `);

  const GET_DOCUMENT_VAULT_ACCOUNT_QUERY = gql(`
    query GetVaultDocuments($sessionToken: ID!, $vaultAccountId: ID!) {
        getVaultDocuments(sessionToken: $sessionToken, vaultAccountId: $vaultAccountId) {
            id
            type
            name
            createdAt
            description
            url
            verifyKyc
            documentDate
        }
    }
`);

  const [
    loginDocumentVaultAccount,
    { loading: loginDocumentVaultAccountLoading, error: loginDocumentVaultAccountError },
  ] = useMutation(LOGIN_VAULT_ACCOUNT_MUTATION);

  const [investmentToken, setInvestmentToken] = useState<string | null>(() => {
    try {
      return JSON.parse(sessionStorage.getItem('investmentToken') || '');
    } catch {
      return null;
    }
  });

  const {
    data: getVaultDocumentsData,
    loading: getVaultDocumentsLoading,
    error: getVaultDocumentsError,
  } = useQuery(GET_DOCUMENT_VAULT_ACCOUNT_QUERY, {
    variables: {
      sessionToken: investmentToken || '',
      vaultAccountId: documentId || '',
    },
    skip: !investmentToken || !documentId,
  });

  if (getVaultDocumentsError?.graphQLErrors?.length) {
    setInvestmentToken(null);
  }

  if (!investmentToken) {
    return (
      <AuthLayout title="Login to view this document">
        <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
          <FormPanel
            loading={loginDocumentVaultAccountLoading}
            error={loginDocumentVaultAccountError}
            onCancel={() => {}}
            onSubmit={data => {
              loginDocumentVaultAccount({
                variables: { email: data.email, password: data.password },
              }).then(({ data }) => {
                setInvestmentToken(data?.loginDocumentVaultAccount?.sessionToken || null);
                sessionStorage.setItem(
                  'investmentToken',
                  JSON.stringify(data?.loginDocumentVaultAccount?.sessionToken || null)
                );
              });
            }}
          >
            <FormInput label="Email" fieldName="email" type="text" defaultValue="" />
            <FormInput label="Password" fieldName="password" type="password" defaultValue="" />
          </FormPanel>
        </div>
      </AuthLayout>
    );
  }

  const documents = getVaultDocumentsData?.getVaultDocuments?.map(document => {
    return {
      ...document,
      description: document?.description || 'No description',
    };
  });

  const groupedDocuments = documents?.reduce((groups, doc) => {
    const year = dayjs(doc.documentDate).get('year') || 'Others';
    return { ...groups, [year]: [...(groups[year] || []), doc] };
  }, {} as Record<string, typeof documents>);

  return (
    <div className="container mx-auto py-16">
      <nav className="fixed top-0 left-0 right-0 bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex h-16 justify-between items-center">
            <a href="https://angellistindia.com" className="flex items-center gap-1">
              <img src={alIndiaLogo} alt="AngelList India" className="h-6 w-auto" />
            </a>
            <Button
              onClick={() => {
                sessionStorage.removeItem('investmentToken');
                window.location.reload();
              }}
              variant="secondary"
              leadingIcon={<ArrowLeftStartOnRectangleIcon className="h-5 w-5" />}
            >
              Logout
            </Button>
          </div>
        </div>
      </nav>
      <div className="mt-8 px-4">
        {getVaultDocumentsLoading && <LoadingIndicator />}
        {getVaultDocumentsError && <ErrorMessage error={getVaultDocumentsError} />}
        <h1 className="text-2xl font-bold mb-4">Documents</h1>
        {!documents?.length ? null : (
          <>
            {Object.entries(groupedDocuments || {}).map(([year, documents]) => (
              <div key={year}>
                <h2 className="text-2xl font-bold my-4">{year}</h2>
                <DataTable
                  data={documents || []}
                  columns={[
                    {
                      label: 'Name',
                      fieldName: 'name',
                    },
                    {
                      label: 'Type',
                      fieldName: 'type',
                      type: 'INVESTMENT_ENTITY_DOCUMENT_TYPE',
                    },
                    {
                      label: 'Added on',
                      fieldName: 'createdAt',
                      type: 'DATE',
                    },
                    {
                      label: 'Description',
                      fieldName: 'description',
                      type: 'STRING',
                    },
                    {
                      label: 'File',
                      fieldName: 'url',
                      type: 'FILE',
                    },
                  ]}
                  emptyListTitle="No documents to show"
                  emptyListDescription="Documents will appear here once they are uploaded."
                />
              </div>
            ))}
          </>
        )}
      </div>
      <div className="mt-8 px-4">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Confidentiality Notice</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  The documents and information contained in this vault are strictly confidential.
                  Unauthorized access, distribution, copying or disclosure of this information is
                  prohibited and may result in legal action. By accessing these documents, you agree
                  to maintain their confidentiality and use them only for their intended purpose.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VaultDocument;
